import React, { useState, useEffect, useContext } from 'react';
import { useParams } from "react-router-dom";
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import PayPal from "./PayPal";
import Loading from "../components/Loading";
import Data from "../../repository";
import AppContext from "../../context";

import styles from '../../styles/Tokens.module.scss';

const checkoutSchema = yup.object({
    purchaser: yup
        .string()
        .email('Please enter a valid email address')
        .required('Email is required')
});

function Checkout() {
    const { id } = useParams();
    const context = useContext(AppContext);

    const [loading, setLoading] = useState(true);
    const [product, setProduct] = useState(null);
    const [code, setCode] = useState("");
    const [discountCode, setDiscountCode] = useState(null);
    const [discountLoading, setDiscountLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [serverError, setServerError] = useState(null);

    const {
        register,
        handleSubmit,
        formState: { errors },
        watch
    } = useForm({
        resolver: yupResolver(checkoutSchema),
        defaultValues: {
            purchaser: ''
        }
    });

    const purchaser = watch('purchaser');

    useEffect(() => {
        fetchProduct();
    }, []);

    const fetchProduct = async () => {
        try {
            const result = await Data.fetchPublicProduct(id);
            setProduct(result.data);
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    }

    const applyCode = async () => {
        // In public gift voucher flow we defer validation to the server at checkout time.
        setDiscountCode({ code });
        setCode("");
    }

    if (loading) return <Loading button={false} />

    const proceedToWorldpay = async (data) => {
        setServerError(null);
        try {
            const checkoutSession = await Data.createWorldpayGiftVoucherCheckout(product._id, discountCode?.code, data.purchaser);
            window.location.href = checkoutSession.data.url;
        } catch (error) {
            setServerError(error.response?.data?.message || 'Failed to proceed to checkout. Please try again.');
            console.log(error);
        }
    }

    return (
        <div className={styles.checkout}>
            <div className={styles.summary}>
                <h2>
                    {product.description?.trim()
                        ? `${product.description}${product.credits > 0 ? ` (${product.credits} ${product.credits > 1 ? "Tokens" : "Token"})` : ''}`
                        : `${product.credits} ${product.credits > 1 ? "Tokens" : "Token"}`}
                </h2>

                {discountCode && typeof discountCode.percentageOff === 'number' ?
                    <p><strike>£{(product.price / 100).toFixed(2)}</strike> £{((product.price / 100) - (product.price / 100) * (discountCode.percentageOff / 100)).toFixed(2)}</p>
                    :
                    <p>£{(product.price / 100).toFixed(2)}</p>
                }
            </div>

            {success ?
                <p className={styles.success}>Your purchase was successful!<br /><br />You will receive an email from us with your gift voucher code shortly.</p>
                :
                <>
                    <div className={styles.discountCode}>
                        {discountCode ?
                            <>
                                <p>Discount Applied: {discountCode.name || discountCode.code}{typeof discountCode.percentageOff === 'number' ? ` (${discountCode.percentageOff}% Off)` : ' (to be applied at checkout)'}</p>
                                <img src={require("../../images/cross.svg").default} alt="Remove" onClick={() => {
                                    setCode("");
                                    setDiscountCode(null);
                                }} />
                            </>
                            :
                            <>
                                <input type="text" placeholder="Discount Code" value={code} onChange={(e) => { return setCode(e.target.value) }} />
                                <button onClick={applyCode} className="btn btnEmpty">Apply</button>
                            </>
                        }
                    </div>
                    {!discountLoading && (
                        context.domain.paymentProcessor === 'Paypal' ?
                            <PayPal product={product} setSuccess={setSuccess} discountCode={discountCode?.code} />
                            : context.domain.paymentProcessor === 'Worldpay' ?
                                <form onSubmit={handleSubmit(proceedToWorldpay)}>
                                    <div className="mb-2">
                                        <input
                                            type="email"
                                            placeholder="Purchaser Email"
                                            {...register('purchaser')}
                                            className={`w-full ${errors.purchaser ? 'input-error' : ''}`}
                                        />
                                        {errors.purchaser && <span className="form-field-error">{errors.purchaser.message}</span>}
                                    </div>
                                    {serverError && <p style={{ color: 'red', marginBottom: '10px' }}>{serverError}</p>}
                                    <button type="submit" className="btn btnPrimary btn w-full mb-4" disabled={!purchaser}>Proceed to Checkout</button>
                                </form>
                                :
                                <p>No payment processor enabled.</p>
                    )}

                    <a href="/gift-vouchers" className="btn btnEmpty">Back</a>
                </>
            }
        </div>
    );
}

export default Checkout;
